// Sound sync: the host's phone tells nearby phones which song and verse the session is on,
// through near-ultrasonic tones. Every symbol is ONE tone out of sixteen (frequency-shift
// keying). Nothing ever plays two tones at once: a small speaker driven with two tones mixes
// them into their difference frequency, which is well within hearing, and summed tones clip.
// A message is seven symbols:
//
//   preamble, session nibble, song position (2 symbols), verse position (2 symbols), checksum
//
// Positions are 1-based so that 0 means "none" (no song / all verses); the song's position is
// its place in the queue, which keeps the numbers small.

export const TONE_SPACING = 120; // Hz between neighbouring symbols (a 40 ms window resolves 25 Hz)
export const TONES = Array.from({ length: 16 }, (_, symbol) => 17600 + symbol * TONE_SPACING); // 17600..19400 Hz
// around and between the tones: where the noise floor is measured
export const GUARD_TONES = [17540, 18020, 18500, 18980, 19460]; // Hz

/**
 * An audio context below this rate cannot carry the tones: oscillators are clamped to half
 * the sample rate (a loud, audible tone) and a microphone at that rate folds them down.
 */
export const MIN_SAMPLE_RATE = 40000;
export const UNSUPPORTED_SAMPLE_RATE = "UnsupportedSampleRateError";

export const SYMBOL_MS = 150; // how long each symbol sounds
export const GAP_MS = 60; // silence between symbols, so equal neighbours stay apart
export const REPEAT_MS = 4000; // how often the host repeats the message
export const FRAME_MS = 40; // how often the listener looks at the spectrum

export const PREAMBLE = 15;
export const MESSAGE_LENGTH = 7;
export const MAX_POSITION = 255;

const WEIGHTS = [3, 5, 7, 11, 13];

export const symbolTone = (symbol) => TONES[symbol];

/** Throws (name UNSUPPORTED_SAMPLE_RATE) when a context's sample rate cannot carry the tones; unknown rates pass. */
export function checkSampleRate(sampleRate) {
  if (!Number.isFinite(sampleRate) || sampleRate >= MIN_SAMPLE_RATE) return;
  const error = new Error(
    `This device's audio runs at ${sampleRate} Hz, too low for the sync tones (a Bluetooth headset or a call in progress can cause this).`
  );
  error.name = UNSUPPORTED_SAMPLE_RATE;
  throw error;
}

/** Four bits derived from the session code, so a message from another room is ignored. */
export const sessionNibble = (code) => {
  let hash = 0;
  for (const ch of (code || "").toUpperCase()) hash = (hash * 31 + ch.charCodeAt(0)) & 0xffff;
  return hash & 15;
};

const checksum = (data) => data.reduce((sum, value, index) => sum + value * WEIGHTS[index], 0) & 15;

const clamp = (value) => Math.max(0, Math.min(MAX_POSITION, Math.trunc(Number(value) || 0)));

export function encodeMessage({ sessionCode, songPosition = 0, versePosition = 0 }) {
  const song = clamp(songPosition);
  const verse = clamp(versePosition);
  const data = [sessionNibble(sessionCode), song >> 4, song & 15, verse >> 4, verse & 15];
  return [PREAMBLE, ...data, checksum(data)];
}

/** The positions carried by a message, or null when it is not a valid message for this session. */
export function decodeMessage(symbols, sessionCode) {
  if (!Array.isArray(symbols) || symbols.length !== MESSAGE_LENGTH || symbols[0] !== PREAMBLE) return null;
  const data = symbols.slice(1, 6);
  if (data[0] !== sessionNibble(sessionCode)) return null;
  if (symbols[6] !== checksum(data)) return null;
  return { songPosition: data[1] * 16 + data[2], versePosition: data[3] * 16 + data[4] };
}
